import { useCallback, useEffect, useState } from "react";
import { useFuneralHome } from "../../context/FuneralHomeContext";
import { AccountingApi } from "../../api/accountingApi";
import { StockData, StockTransferData } from "../../models/StockData";
import { AccountingEntity } from "../../models/AccountingEntity";
import FuneralHomeHandler from "../FuneralHomeHandler";
import { useTranslation } from "react-i18next";
import AccountingEntityDropDown from "../dropdown/AccountingEntityDropdown";

function StockTransfer() {
  const { accountingEntities, selectedAccountingEntity } = useFuneralHome();
  const { t } = useTranslation();

  const [stockTransferOverview, setStockTransferOverview] = useState<
    StockTransferData[]
  >([]);
  const [selectedToAccountingEntity, setSelectedToAccountingEntity] =
    useState<AccountingEntity | null>(null);
  const [isLoadingStock, setIsLoadingStock] = useState<boolean>(false);
  const [errorStock, setErrorStock] = useState<string | null>(null);

  const [errorTransfer, setErrorTransfer] = useState<string | null>(null);
  const [successTransfer, setSuccessTransfer] = useState<boolean>(false);

  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const fetchStockOverview = useCallback(() => {
    if (!selectedAccountingEntity) return;

    setIsLoadingStock(true);
    setSelectedToAccountingEntity(null);
    setStockTransferOverview([]);
    setErrorStock(null);
    AccountingApi.fetchStockOverview(
      selectedAccountingEntity.COMPANY_ID,
      selectedAccountingEntity.COSTCODE
    )
      .then((data) => {
        const stockTransferData: StockTransferData[] = data
          .filter((item: StockData) => item.inventory !== 0)
          .map((item: StockData) => ({
            ...item,
            transferQuantity: 0, // Set correctionQuantity as inventory
          }));
        setStockTransferOverview(stockTransferData);
      })
      .catch((error) => {
        let errorMessage = error.message;
        if (error.response?.data.ERROR) {
          errorMessage = error.response.data.ERROR;
        }
        setErrorStock(errorMessage);
      })
      .finally(() => {
        setIsLoadingStock(false);
      });
  }, [selectedAccountingEntity]);

  const handleTransfer = () => {
    if (!selectedAccountingEntity || !selectedToAccountingEntity) {
      return;
    }

    setIsProcessing(true);
    setErrorTransfer(null);
    AccountingApi.createStockTransfer(
      selectedAccountingEntity.COMPANY_ID,
      selectedAccountingEntity.COSTCODE,
      selectedToAccountingEntity.COSTCODE,
      stockTransferOverview
    )
      .then((data) => {
        setSuccessTransfer(true);
      })
      .catch((error) => {
        let errorMessage = error.message;
        if (error.response?.data.ERROR) {
          errorMessage = error.response.data.ERROR;
        }
        setErrorTransfer(errorMessage);
      })
      .finally(() => {
        fetchStockOverview();
        setIsProcessing(false);
      });
  };

  const handleToAccountingEntityChange = (selectedId: string) => {
    const selectedEntity = accountingEntities.find(
      (entity) => `${entity.COMPANY_ID}-${entity.COSTCODE}` === selectedId
    );
    setSuccessTransfer(false);
    setSelectedToAccountingEntity(selectedEntity || null);
  };

  const isFormValid = () => {
    return (
      isAccountingEntityValid() &&
      stockTransferOverview.some(
        (item) =>
          item.transferQuantity > 0 && item.transferQuantity <= item.inventory
      )
    );
  };

  const isAccountingEntityValid = () => {
    return (
      selectedToAccountingEntity &&
      selectedToAccountingEntity.COMPANY_ID === selectedAccountingEntity?.COMPANY_ID &&
      selectedToAccountingEntity.COSTCODE !== selectedAccountingEntity?.COSTCODE
    );
  };

  const handleQuantityChange = (id: string, value: number) => {
    const updatedData = stockTransferOverview.map((item) =>
      item.id === id ? { ...item, transferQuantity: value } : item
    );
    setStockTransferOverview(updatedData);
  };

  // Effect to run when the component mounts or when selectedAccountingEntity changes
  useEffect(() => {
    fetchStockOverview(); // Fetch data
  }, [fetchStockOverview, selectedAccountingEntity]); // Dependency array

  return (
    <div className="container mt-5">
      <h1>{t("transfer.title")}</h1>
      <p>{t("transfer.information")}</p>

      <FuneralHomeHandler />

      {successTransfer && (
        <p className="text-success mt-4">{t("transfer.success")}</p>
      )}
      {/* Display Stock Overview Data */}
      {selectedAccountingEntity != null && (
        <div className="mt-4">
          {!isLoadingStock && stockTransferOverview.length === 0 && (
            <p className="text-info">{t("stockoverview.no_stock")}</p>
          )}
          {/* Loading and Error States */}
          {isLoadingStock && <p className="text-info">{t("loading.stock")}</p>}
          {errorStock && (
            <p className="text-danger">
              {t("error.stock", { message: errorStock })}
            </p>
          )}
          {isProcessing && <p className="text-info">{t("loading.request")}</p>}

          {stockTransferOverview.length > 0 && (
            <>
              <table className="mt-4 table table-striped">
                <thead>
                  <tr>
                    <th>{t("stockoverview.table.sereninumber")}</th>
                    <th>{t("stockoverview.table.suppliernumber")}</th>
                    <th>{t("stockoverview.table.description")}</th>
                    <th>{t("stockoverview.table.category")}</th>
                    <th>{t("stockoverview.table.amount")}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {stockTransferOverview.map((item) => (
                    <tr key={item.id}>
                      <td>{item.no}</td>
                      <td>{item.no2}</td>
                      <td>{item.description}</td>
                      <td>{t("productcategory." + item.itemCategoryCode)}</td>
                      <td>{item.inventory}</td>
                      <td>
                        <input
                          type="number"
                          className="form-control me-2"
                          placeholder={t("purchaseorder.table.quantity")}
                          min={0}
                          max={item.inventory}
                          value={item.transferQuantity}
                          onChange={(e) =>
                            handleQuantityChange(item.id, +e.target.value)
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Select the accounting entity to transfer to */}
              <div className="form-group mt-4">
                <label htmlFor="accountingEntitySelect">
                  {t("transfer.receivingfuneralhome")}
                </label>
                <AccountingEntityDropDown
                  selectedEntityId={
                    selectedToAccountingEntity ? `${selectedToAccountingEntity.COMPANY_ID}-${selectedToAccountingEntity.COSTCODE}` : ""
                  }
                  handleChange={(value) => handleToAccountingEntityChange(value)}
                  entities={accountingEntities}
                />
                {!isAccountingEntityValid() && (
                  <p className="text-danger">
                    {t("transfer.mismatchedfuneralhome")}
                  </p>
                )}
              </div>

              {errorTransfer && (
                <p className="text-danger">
                  {t("error.transfer", { message: errorTransfer })}
                </p>
              )}

              <div>
                {/* Start transfer button */}
                {stockTransferOverview.length > 0 && (
                  <button
                    className="btn btn-primary mt-4 mr-2"
                    onClick={handleTransfer}
                    disabled={isProcessing || !isFormValid()}
                  >
                    {t("transfer.submit")}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
export default StockTransfer;
